import { useStyletron } from '@thanx/uikit/theme'
import { useGetMenuErrorsQuery } from 'api/menuErrors'
import PageHeader from 'components/PageHeader'
import palette from 'constants/palette'
import useFlag from 'hooks/useFlag'
import { buildTranslate } from 'locales'
import React from 'react'
import { Container } from 'react-bootstrap-five'
import Helmet from 'react-helmet'
import HelpLinks from 'scenes/OperationsCenter/Locations/HelpLinks'
import {
  ErrorEmptyState,
  HealthyEmptyState,
  OloEmptyState,
  PreLaunchEmptyState,
  ProviderSkipEmptyState,
} from './EmptyStates'
import MenuErrorsList from './MenuErrorsList'

const t = buildTranslate('pos_configurations')

// Static informational banner — `role="status"` was originally on the
// wrapper, which made the live-region polite-announce on every render of
// this never-changing copy (WCAG 4.1.2 false positive). The visual cue
// (info icon + accent) carries the affordance without needing AT to
// announce anything. If product wants a true live region later — for
// example a dismissable warning when a new error category lands — that
// should sit on the inner text node so only the actual update is
// announced, not the chrome around it.
const NotificationBanner = () => {
  const [css] = useStyletron()
  return (
    <div
      className={`d-flex align-items-start p-m mb-l ${css({
        backgroundColor: palette.belizeHole10,
        borderRadius: '4px',
      })}`}
    >
      <i
        className={`fa fa-info-circle mr-m mt-xxs ${css({
          color: palette.belizeHole,
          fontSize: '20px',
        })}`}
        aria-hidden="true"
      />
      <p className={`body-text-4 mb-0 ${css({ color: palette.grey70 })}`}>
        {t('banner_message')}
      </p>
    </div>
  )
}

const MenuErrorsListView = () => {
  const [css] = useStyletron()
  const menuErrorsEnabled = useFlag('operations-center-menu-errors', false)
  const { data, isLoading, isError } = useGetMenuErrorsQuery(undefined, {
    skip: !menuErrorsEnabled,
  })

  // Single assignment (not early returns) keeps this within qlty's
  // return-statement budget; ordering matters — flag gate, then the
  // explicit error state so a failed request shows a message rather
  // than a blank body, then loading, then the payload-driven states.
  let body: React.ReactNode = null
  if (!menuErrorsEnabled) {
    body = <PreLaunchEmptyState />
  } else if (isError) {
    body = <ErrorEmptyState />
  } else if (isLoading || !data) {
    // Live-region loading signal rather than a blank content area: a
    // bare gap reads as "broken" to sighted users and is invisible to
    // AT (WCAG 4.1.3). Mirrors MenuCard's `<p role="status">` pattern.
    body = (
      <p
        role="status"
        className={`body-text-3 ${css({ color: palette.grey70 })}`}
      >
        {t('loading') as string}
      </p>
    )
  } else if (data.skippedProvider === 'olo') {
    body = <OloEmptyState />
  } else if (data.skippedProvider) {
    body = <ProviderSkipEmptyState provider={data.skippedProvider} />
  } else if (data.menus.length === 0) {
    body = <HealthyEmptyState />
  } else {
    body = <MenuErrorsList menus={data.menus} providers={data.providers} />
  }

  return (
    <PageHeader title={t('title')}>
      <Helmet>
        <title>{t('page_title')}</title>
      </Helmet>
      <Container>
        <NotificationBanner />
        {body}
        <div className="mt-xl">
          <HelpLinks />
        </div>
      </Container>
    </PageHeader>
  )
}

const PosConfigurations = MenuErrorsListView

export default PosConfigurations
